import React, { useCallback, useEffect, useState } from 'react';
import { hr, BASE } from '../../face/hrApi';

const ago = (d) => {
  if (!d) return 'never';
  const s = Math.max(0, Math.round((Date.now() - new Date(d).getTime()) / 1000));
  if (s < 90) return 'just now';
  if (s < 5400) return `${Math.round(s / 60)} min ago`;
  if (s < 129600) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86400)} days ago`;
};

export default function FaceDevicesTab() {
  const [devices, setDevices] = useState(null);
  const [locations, setLocations] = useState([]);
  const [form, setForm] = useState({ name: '', username: '', location_id: '' });
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState('');
  const [secret, setSecret] = useState(null); // { title, username, password }
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try { setDevices((await hr('/devices')).devices); } catch (e) { setErr(e.message); }
  }, []);
  useEffect(() => {
    load();
    (async () => {
      try {
        const res = await fetch(`${BASE}/locations`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
        const d = await res.json();
        const list = Array.isArray(d) ? d : (d.locations || []);
        setLocations(list.filter((l) => l && l.id));
      } catch (_) { /* location is optional */ }
    })();
  }, [load]);

  const create = async (e) => {
    e.preventDefault(); setErr(''); setCreating(true);
    try {
      const body = { name: form.name.trim(), username: form.username.trim().toLowerCase() };
      if (form.location_id) body.location_id = Number(form.location_id);
      const r = await hr('/devices', { method: 'POST', body });
      setSecret({ title: 'Device created', username: r.device.username, password: r.password });
      setForm({ name: '', username: '', location_id: '' });
      load();
    } catch (ex) { setErr(ex.message); }
    finally { setCreating(false); }
  };
  const toggle = async (d) => {
    setBusyId(d.id);
    try { await hr(`/devices/${d.id}`, { method: 'PUT', body: { is_active: !d.is_active } }); await load(); }
    catch (ex) { setErr(ex.message); } finally { setBusyId(null); }
  };
  const reset = async (d) => {
    if (!window.confirm(`Reset the password for "${d.name}"? The kiosk will be signed out and must sign in again.`)) return;
    setBusyId(d.id);
    try { const r = await hr(`/devices/${d.id}/reset-password`, { method: 'POST', body: {} }); setSecret({ title: 'Password reset', username: r.device.username, password: r.password }); await load(); }
    catch (ex) { setErr(ex.message); } finally { setBusyId(null); }
  };
  const copy = (t) => { try { navigator.clipboard.writeText(t); } catch (_) { /* ignore */ } };

  return (
    <div>
      {secret && (
        <div className="fc-callout fc-ok">
          <b>{secret.title}.</b> Write these down now — the password is shown only once.
          <div className="fc-secret">
            <div><span>Username</span><code>{secret.username}</code></div>
            <div><span>Password</span><code>{secret.password}</code> <button className="fc-link" onClick={() => copy(secret.password)}>copy</button></div>
            <div><span>Kiosk address</span><code>{window.location.origin}/kiosk</code></div>
          </div>
          <button className="fc-btn fc-btn-ghost" onClick={() => setSecret(null)}>I have saved it</button>
        </div>
      )}
      {err && <div className="fc-callout fc-bad">{err}</div>}

      <form className="fc-card fc-form" onSubmit={create}>
        <h3>Add a kiosk device</h3>
        <p className="fc-muted">One account per kiosk screen. It can only run the kiosk — it is not an employee and cannot open any other part of the system.</p>
        <div className="fc-grid">
          <label>Name<input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Service Centre tablet" required /></label>
          <label>Username<input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="sc-kiosk-1" pattern="[A-Za-z0-9][A-Za-z0-9._\-]{2,39}" title="3-40 letters, numbers, . _ -" required /></label>
          {locations.length > 0 && (
            <label>Location
              <select value={form.location_id} onChange={(e) => setForm({ ...form, location_id: e.target.value })}>
                <option value="">— none —</option>
                {locations.map((l) => <option key={l.id} value={l.id}>{l.name || l.city}</option>)}
              </select>
            </label>
          )}
        </div>
        <button className="fc-btn fc-btn-red" disabled={creating}>{creating ? 'Creating…' : 'Create device'}</button>
      </form>

      <div className="fc-card">
        <h3>Devices</h3>
        {!devices ? <div className="fc-muted">Loading…</div> : devices.length === 0 ? <div className="fc-muted">No devices yet.</div> : (
          <div className="fc-table-wrap">
            <table className="fc-table">
              <thead><tr><th>Name</th><th>Username</th><th>Location</th><th>Last seen</th><th>24h</th><th>Status</th><th /></tr></thead>
              <tbody>
                {devices.map((d) => (
                  <tr key={d.id}>
                    <td><b>{d.name}</b></td>
                    <td><code>{d.username}</code></td>
                    <td>{d.location_name || d.location_city || '—'}</td>
                    <td><span className={`fc-dot ${d.online ? 'on' : ''}`} />{ago(d.last_seen)}{d.last_ip ? <div className="fc-muted fc-small">{d.last_ip}</div> : null}</td>
                    <td>{d.events_24h}</td>
                    <td><span className={`fc-pill ${d.is_active ? 'fc-pill-ok' : 'fc-pill-off'}`}>{d.is_active ? 'Active' : 'Disabled'}</span></td>
                    <td className="fc-actions">
                      <button className="fc-btn fc-btn-ghost fc-btn-sm" disabled={busyId === d.id} onClick={() => toggle(d)}>{d.is_active ? 'Disable' : 'Enable'}</button>
                      <button className="fc-btn fc-btn-ghost fc-btn-sm" disabled={busyId === d.id} onClick={() => reset(d)}>Reset password</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
